import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Paper,
} from '@mui/material';
import {
  PersonAdd,
  Person,
  AccessTime,
  LocationOn,
  Phone,
} from '@mui/icons-material';

const Bookings: React.FC = () => {
  const handleNewPatientBooking = () => {
    window.open('https://appts.au.visitbase.link/ob/p1.php?h=healthbychiro&pttype=new', '_blank');
  };

  const handleExistingPatientBooking = () => {
    window.open('https://appts.au.visitbase.link/ob/p1.php?h=healthbychiro', '_blank');
  };

  return (
    <Box sx={{ pt: 4, pb: 8 }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h2"
            component="h1"
            sx={{
              fontWeight: 700,
              mb: 3,
              background: 'linear-gradient(45deg, #2c3e50, #34495e)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Book Your Appointment
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'text.secondary',
              mb: 4,
              fontWeight: 400,
            }}
          >
            Take the first step towards better health and wellbeing
          </Typography>
        </Box>

        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'primary.light',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <PersonAdd sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  New Patients
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.7 }}
                >
                  First time visiting us? Welcome! Book your initial consultation 
                  and comprehensive assessment with our experienced team.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleNewPatientBooking}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    background: 'linear-gradient(45deg, #2c3e50, #34495e)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #34495e, #2c3e50)',
                    },
                  }}
                >
                  Book New Patient
                </Button>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card
              sx={{
                height: '100%',
                boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                },
              }}
            >
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    backgroundColor: 'secondary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                  }}
                >
                  <Person sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 600, mb: 2 }}>
                  Existing Patients
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: 'text.secondary', mb: 4, lineHeight: 1.7 }}
                >
                  Ready for your next appointment? Book your follow-up session 
                  or ongoing treatment with ease.
                </Typography>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleExistingPatientBooking}
                  sx={{
                    py: 1.5,
                    px: 4,
                    borderRadius: 2,
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    textTransform: 'none',
                    backgroundColor: 'secondary.main',
                    '&:hover': {
                      backgroundColor: 'secondary.dark',
                    },
                  }}
                >
                  Book Existing Patient
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            backgroundColor: '#f8f9fa',
            border: '1px solid #e9ecef',
          }}
        >
          <Typography
            variant="h5"
            sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}
          >
            Important Information
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <AccessTime sx={{ color: 'primary.main', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Appointment Duration
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', pl: 4 }}>
                Initial consultations: 60 minutes<br />
                Follow-up appointments: 30-45 minutes
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Phone sx={{ color: 'primary.main', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Need Help?
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', pl: 4 }}>
                Call us at (03) 9743 5510 if you need assistance with booking or have questions.
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <LocationOn sx={{ color: 'primary.main', mr: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Location
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', pl: 4 }}>
                477 High Street, Melton VIC 3337<br />
                Free parking available
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 2 }}>
            By booking an appointment, you agree to our terms of service and privacy policy.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Please arrive 10 minutes early for your appointment to complete any necessary paperwork.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default Bookings;